import PropTypes from "prop-types";
import React, { useState } from "react";

import { classNames } from "@swa-ui/string";

import { colors } from "../defines";
import { getColorVariable } from "../getColorVariable";
import { gradientPropTypes } from "../gradients/gradientConfig";
import { Image, imageFetchPriorityPropType, imageSourcesPropType } from "../Image";
import styles from "./Background.module.scss";

const HEIGHTS_LINE = "2px";
const HEIGHTS_LINE_HEIGHT = "2rem";

// TODO get rid of background color tokens: cmp-core-color-background-${color}
/**
 * The Background component renders a background for a component. The background can either be an
 * image and/or a background color. For backgrounds without imageUrl, the background can be a shape,
 * like a circle.
 */
export const Background = (props) => {
  const {
    alt,
    backgroundAttachment = "fixed",
    backgroundPosition = "center top",
    border,
    className,
    color = "transparent",
    fetchPriority = "auto",
    gradient,
    height,
    imageUrl = "",
    immediate,
    shape = "rectangle",
    sources,
    size = "cover",
    style,
    watermark,
  } = props;
  const isImage = imageUrl?.length > 0;
  const [currentOpacity, setCurrentOpacity] = useState(() => (isImage ? 0 : 1));

  return (
    <div {...getProps()}>
      {isImage && (
        <div className={styles.backgroundImageContainer}>
          <Image {...getImageProps()} />
        </div>
      )}
      {renderForeground()}
    </div>
  );

  function renderForeground() {
    const foregroundContent = (
      <>
        {watermark && renderWatermark()}
        {props.children}
      </>
    );

    return isImage ? (
      <div className={isImage && styles.foregroundContainer}>{foregroundContent}</div>
    ) : (
      foregroundContent
    );
  }

  function renderWatermark() {
    return (
      <div className={styles.watermark}>
        <div className={styles.content}>{watermark}</div>
      </div>
    );
  }

  function getProps() {
    return {
      className: getClass(),
      "data-test": props["data-test"],
      style: gradient
        ? {
            backgroundAttachment,
            backgroundImage: gradient,
            backgroundPosition,
            ...getStyle(),
          }
        : getStyle(),
    };
  }

  function getImageProps() {
    return {
      alt: alt ?? imageUrl,
      className: styles.image,
      fetchPriority,
      onLoad: handleImageLoad,
      sources,
      src: imageUrl,
    };
  }

  function getClass() {
    const isWidthNeeded = height && (shape === "circle" || shape === "square");

    return classNames(className, {
      [styles.backgroundColor]: color,
      [styles.borderColor]: border,
      [styles.borderRadius]: border?.radius,
      [styles.borderStyle]: border,
      [styles.borderWidth]: border,
      [styles.capsule]: shape === "capsule",
      [styles.circle]: shape === "circle",
      [styles.contain]: isImage && size === "contain",
      [styles.cover]: isImage && size === "cover",
      [styles.ellipse]: shape === "ellipse",
      [styles.height]: height,
      [styles.image]: isImage,
      [styles.immediate]: isImage && immediate,
      [styles.roundedRectangle]: shape === "roundedRectangle",
      [styles.shape]: !isImage && !watermark,
      [styles.square]: shape === "square",
      [styles.width]: isWidthNeeded && height !== "line" && height !== "lineHeight",
    });
  }

  function getStyle() {
    const borderColor = border?.color ?? "neutral";
    const borderRadius = border?.radius;
    const borderStyle = border?.style ?? "solid";
    const borderWidth = border?.width ?? "small";
    const heights = {
      line: HEIGHTS_LINE,
      lineHeight: HEIGHTS_LINE_HEIGHT,
    };
    const backgroundSize =
      heights[height] || getHeight() || `var(--cmp-core-background-height-${height}-units-rem)`;

    return {
      "--background-color": getColor(),
      "--border-color": getColor(borderColor),
      "--border-radius":
        borderRadius && `var(--cmp-core-background-border-${border.radius}-units-px)`,
      "--border-style": borderStyle,
      "--border-width": `var(--cmp-core-background-border-${borderWidth}-units-px)`,
      opacity: currentOpacity,
      ...style,
      ...(height && { "--height": backgroundSize, "--width": backgroundSize }),
    };
  }

  function getColor(workingColor = color) {
    return colors[workingColor] ?? colors[`${workingColor}-500`] ?? getColorVariable(workingColor);
  }

  function getHeight() {
    return typeof height === "number" || Number(height) ? `${Number(height)}px` : undefined;
  }

  function handleImageLoad() {
    setCurrentOpacity(1);
  }
};

Background.displayName = "Background";

export const backgroundPropTypes = {
  /** Alternate text for the background image if provided. */
  alt: PropTypes.string,

  /** Standard CSS background attachment property. */
  backgroundAttachment: PropTypes.string,

  /** Standard CSS background position property. */
  backgroundPosition: PropTypes.string,

  /** Optional border to surround shape. Not applied to images. */
  border: PropTypes.shape({
    color: PropTypes.string,
    radius: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),
    style: PropTypes.oneOf([
      "dashed",
      "dotted",
      "double",
      "groove",
      "hidden",
      "inset",
      "outset",
      "ridge",
      "solid",
    ]),
    width: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),
  }),

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Optional color definition to be given for background color. */
  color: PropTypes.string,

  /**
   * Optional priority for loading the background image. Set to high for images that need to be loaded
   * quickly such as the page's hero image.
   */
  fetchPriority: imageFetchPriorityPropType,

  /**
   * Gradient definition to be used for CSS backgroundImage property.
   * Examples:
   * getLinearGradient("summit-silver")
   * getLinearGradient("bold-blue", "to top")
   * "linear-gradient(90deg, var(--primary-500), var(--primary-700))"
   */
  gradient: PropTypes.oneOfType([PropTypes.oneOf(gradientPropTypes), PropTypes.string]),

  /**
   * Optional minimum height to be given to Background. For circle or square shapes, height will
   * also define the background's width.
   */
  height: PropTypes.oneOfType([
    PropTypes.oneOf(["large", "line", "lineHeight", "medium", "small", "xlarge", "xsmall"]),
    PropTypes.number,
    PropTypes.string,
  ]),

  /** Sets the background image URL. */
  imageUrl: PropTypes.string,

  /** If immediate is true, no animation will occur and the background will be displayed immediately. */
  immediate: PropTypes.bool,

  /** Defines if a specific background shape is desired such as a circle or capsule. */
  shape: PropTypes.oneOf([
    "capsule",
    "circle",
    "ellipse",
    "rectangle",
    "roundedRectangle",
    "square",
  ]),

  /**
   * Size determines how the background will fill the window. For more information, research
   * background properties contain and cover. For cover, the background-position is set to center.
   */
  size: PropTypes.oneOf(["contain", "cover"]),

  /** The list of sources to use when rendering the background image */
  sources: imageSourcesPropType,

  /** CSS styles to be applied to Background outer element. */
  style: PropTypes.object,

  /**
   * Content to be displayed for an element's background. This would typically be a light colored
   * image, but can be whatever is needed.
   */
  watermark: PropTypes.node,
};

Background.propTypes = backgroundPropTypes;
